import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../localization/components/AppLocalized"

import { MenuDisplaySlide } from "./MenuDisplaySlide"
import { MENU_DISPLAY_COLORS, MENU_DISPLAY_SLIDE_TRANSITION_MS, MenuDisplayItem } from "./menuDisplayData"

type StyleProps = Readonly<{
    offsetIndex: number
    animate: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { offsetIndex, animate }) => ({
    featured: {
        flex: "1 1 auto",
        minWidth: 0,
        padding: "2.25em 0 2.5em",
        overflow: "hidden",
    },
    viewport: {
        flex: "1 1 auto",
        minWidth: 0,
        overflow: "hidden",
    },
    track: {
        height: "100%",
        transform: `translateX(-${offsetIndex * 100}%)`,
        transition: animate ? `transform ${MENU_DISPLAY_SLIDE_TRANSITION_MS}ms cubic-bezier(.65, 0, .35, 1)` : "none",
    },
    pagination: {
        padding: "1.5em 3.5em 0",
    },
    dot: {
        width: "1.25em",
        height: "1.25em",
        borderRadius: ".625em",
        backgroundColor: MENU_DISPLAY_COLORS.darkGreen,
        opacity: 0.4,
        transition: "width .4s ease, opacity .4s ease",
    },
    dotActive: {
        width: "6.125em",
        opacity: 1,
    },
    // em-based, so the text scales with the stage; no Typography variant can track the stage size.
    menusText: {
        color: theme.palette.tailwind.slate[700],
        fontWeight: 600,
        fontSize: "1.5em",
        lineHeight: 1,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        opacity: 0.6,
    },
}))

type MenuDisplayFeaturedProps = Readonly<{
    items: ReadonlyArray<MenuDisplayItem>
    offsetIndex: number
    activeIndex: number
    animate: boolean
}>

export function MenuDisplayFeatured({ items, offsetIndex, activeIndex, animate }: MenuDisplayFeaturedProps) {
    const { classes, cx } = useStyles({ offsetIndex, animate })

    // The first dish is cloned at the end so the strip can keep sliding left and loop seamlessly.
    const slides = [...items, items[0]]

    return (
        <Stack direction="column" className={classes.featured}>
            <Box className={classes.viewport}>
                <Stack direction="row" className={classes.track}>
                    {slides.map((item, index) => (
                        <MenuDisplaySlide key={index} item={item} />
                    ))}
                </Stack>
            </Box>
            <Stack direction="row" alignItems="center" spacing="1.5em" className={classes.pagination}>
                <Stack direction="row" alignItems="center" spacing=".75em">
                    {items.map((item, index) => (
                        <Box key={item.id} className={cx(classes.dot, index === activeIndex && classes.dotActive)} />
                    ))}
                </Stack>
                <LocalizedStrict
                    id="public-menu-display-pagination-label"
                    vars={{ current: activeIndex + 1, total: items.length }}
                >
                    <Typography className={classes.menusText}>
                        {activeIndex + 1} of {items.length} menus
                    </Typography>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
